import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Drawer } from '@/components/ui/drawer'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Icons } from '@/theme/Images'
import InputBlock from '../rules-page/input-block'
import SelectBlock from '../rules-page/select-block'
import RulesDialogHeader from '../rules-page/dialog-header/rules-dialog-header'
import { Divider } from '@/components/divider'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { validateCsv } from './validation'
import { toast } from 'sonner'
import { ToastMessage } from '@/components/toast-message'
import { TOASTS } from '@/data/toast-messages'
import { SearchInput } from '@/components/search-input'
import { addBusiness as apiAddBusiness, addBusinessLocation as apiAddBusinessLocation } from '@/api/partners'
import { ETDate } from '@/utils/et-date'
import ContractGroup from './contract-group'
import { splitAndPrefix } from '@/utils/mid-utils'

const SectionHeader = ({ text }) => (
  <div
    className="w-full flex items-center"
    style={{
      background: 'var(--Light-Background, #F5F7F8)',
      height: '28px',
      padding: '6px 20px',
      gap: '6px',
      color: '#667085',
      fontSize: '12px',
    }}
  >
    <span className="font-medium">{text}</span>
  </div>
)

SectionHeader.propTypes = {
  text: PropTypes.string,
}

const SubsectionHeader = ({ text }) => (
  <div
    className="w-full"
    style={{
      color: '#101828',
      fontSize: '16px',
    }}
  >
    <span className="font-semibold">{text}</span>
  </div>
)

SubsectionHeader.propTypes = {
  text: PropTypes.string,
}

const PartnerHeader = ({ partnerNumber, onDelete, canDelete }) => (
  <div className="w-full flex items-center justify-between">
    <span
      className="font-semibold"
      style={{
        color: '#101828',
        fontSize: '16px',
      }}
    >
      Partner {partnerNumber}
    </span>
    {canDelete && (
      <button
        type="button"
        onClick={onDelete}
        className="bg-transparent border-none p-0 cursor-pointer hover:opacity-80 transition-opacity"
        style={{
          color: 'var(--Brand-700, #467083)',
          fontSize: '14px',
          fontWeight: '600',
          textDecoration: 'underline',
          textUnderlinePosition: 'from-font',
        }}
      >
        Delete Partner
      </button>
    )}
  </div>
)

PartnerHeader.propTypes = {
  partnerNumber: PropTypes.number,
  onDelete: PropTypes.func,
  canDelete: PropTypes.bool,
}

// Component to render individual partner sections
const PartnerSection = ({ partner, partnerIndex, updatePartner, formik }) => {
  // Create a mock formik object for this specific partner
  const partnerFormik = {
    values: partner,
    errors: formik.errors.partners?.[partnerIndex] || {},
    touched: formik.touched.partners?.[partnerIndex] || {},
    setFieldValue: (field, value) => {
      updatePartner(partner.id, field, value)
      // Keep Formik's values in sync so validation runs on current data
      formik.setFieldValue(`partners.${partnerIndex}.${field}`, value)
    },
    setFieldTouched: (field, touched = true) => {
      // Update the touched state for this partner
      const updatedTouched = { ...formik.touched }
      if (!Array.isArray(updatedTouched.partners)) updatedTouched.partners = []
      if (!updatedTouched.partners[partnerIndex]) updatedTouched.partners[partnerIndex] = {}
      updatedTouched.partners[partnerIndex][field] = touched
      formik.setFieldTouched(`partners.${partnerIndex}.${field}`, touched)
    },
    getFieldProps: field => ({
      name: field,
      value: partner[field] || '',
      onChange: e => {
        const val = e?.target ? e.target.value : e
        updatePartner(partner.id, field, val)
        formik.setFieldValue(`partners.${partnerIndex}.${field}`, val)
      },
      onBlur: () => {
        const updatedTouched = { ...formik.touched }
        if (!Array.isArray(updatedTouched.partners)) updatedTouched.partners = []
        if (!updatedTouched.partners[partnerIndex]) updatedTouched.partners[partnerIndex] = {}
        updatedTouched.partners[partnerIndex][field] = true
        formik.setFieldTouched(`partners.${partnerIndex}.${field}`, true)
      },
    }),
  }

  return (
    <div className="flex flex-col gap-6">
      <InputBlock
        label="Partner Name*"
        id="partnerName"
        placeholder="Enter partner name"
        formik={partnerFormik}
        showValidation={true}
        iconPosition="right"
      />
      <SelectBlock
        label="Partner Type*"
        id="partnerType"
        placeholder="Select partner type"
        formik={partnerFormik}
        options={[
          { value: 'DEALERSHIP', label: 'Dealership' },
          { value: 'AUTO_CARE', label: 'Auto Care' },
          { value: 'MERCHANT', label: 'Merchant' },
        ]}
        showValidation={true}
      />
      <InputBlock
        label="Address Line 1"
        id="street1"
        placeholder="Enter address line 1"
        formik={partnerFormik}
        showValidation={true}
        iconPosition="right"
      />
      <InputBlock
        label="Address Line 2"
        id="street2"
        placeholder="Enter address line 2"
        formik={partnerFormik}
        showValidation={true}
        iconPosition="right"
      />
      <div className="grid grid-cols-2 gap-4">
        <InputBlock
          label="City"
          id="city"
          placeholder="Enter city"
          formik={partnerFormik}
          showValidation={true}
          iconPosition="right"
        />
        <SelectBlock
          label="State"
          id="state"
          placeholder="Select state"
          formik={partnerFormik}
          options={[
            { value: 'AL', label: 'Alabama' },
            { value: 'AK', label: 'Alaska' },
            { value: 'AZ', label: 'Arizona' },
            { value: 'AR', label: 'Arkansas' },
            { value: 'CA', label: 'California' },
            { value: 'CO', label: 'Colorado' },
            { value: 'CT', label: 'Connecticut' },
            { value: 'DE', label: 'Delaware' },
            { value: 'FL', label: 'Florida' },
            { value: 'GA', label: 'Georgia' },
            { value: 'HI', label: 'Hawaii' },
            { value: 'ID', label: 'Idaho' },
            { value: 'IL', label: 'Illinois' },
            { value: 'IN', label: 'Indiana' },
            { value: 'IA', label: 'Iowa' },
            { value: 'KS', label: 'Kansas' },
            { value: 'KY', label: 'Kentucky' },
            { value: 'LA', label: 'Louisiana' },
            { value: 'ME', label: 'Maine' },
            { value: 'MD', label: 'Maryland' },
            { value: 'MA', label: 'Massachusetts' },
            { value: 'MI', label: 'Michigan' },
            { value: 'MN', label: 'Minnesota' },
            { value: 'MS', label: 'Mississippi' },
            { value: 'MO', label: 'Missouri' },
            { value: 'MT', label: 'Montana' },
            { value: 'NE', label: 'Nebraska' },
            { value: 'NV', label: 'Nevada' },
            { value: 'NH', label: 'New Hampshire' },
            { value: 'NJ', label: 'New Jersey' },
            { value: 'NM', label: 'New Mexico' },
            { value: 'NY', label: 'New York' },
            { value: 'NC', label: 'North Carolina' },
            { value: 'ND', label: 'North Dakota' },
            { value: 'OH', label: 'Ohio' },
            { value: 'OK', label: 'Oklahoma' },
            { value: 'OR', label: 'Oregon' },
            { value: 'PA', label: 'Pennsylvania' },
            { value: 'RI', label: 'Rhode Island' },
            { value: 'SC', label: 'South Carolina' },
            { value: 'SD', label: 'South Dakota' },
            { value: 'TN', label: 'Tennessee' },
            { value: 'TX', label: 'Texas' },
            { value: 'UT', label: 'Utah' },
            { value: 'VT', label: 'Vermont' },
            { value: 'VA', label: 'Virginia' },
            { value: 'WA', label: 'Washington' },
            { value: 'WV', label: 'West Virginia' },
            { value: 'WI', label: 'Wisconsin' },
            { value: 'WY', label: 'Wyoming' },
          ]}
          showValidation={true}
        />
      </div>
      <InputBlock
        label="ZIP Code"
        id="zip"
        placeholder="Enter ZIP code"
        formik={partnerFormik}
        showValidation={true}
        iconPosition="right"
      />
      <InputBlock
        label="Spade Counter Party ID(s)"
        id="spadeCounterPartyId"
        placeholder="Enter Spade counterparty_id"
        formik={partnerFormik}
        showValidation={true}
        iconPosition="right"
      />
      <InputBlock
        label="Spade Location ID(s)"
        id="spadeLocationId"
        placeholder="Enter Spade location_id"
        formik={partnerFormik}
        showValidation={true}
        iconPosition="right"
      />
      <InputBlock
        label="Highnote ID(s)"
        id="highnoteId"
        placeholder="Enter Highnote ID"
        formik={partnerFormik}
        showValidation={true}
        iconPosition="right"
      />
    </div>
  )
}

PartnerSection.propTypes = {
  partner: PropTypes.object,
  partnerIndex: PropTypes.number,
  updatePartner: PropTypes.func,
  formik: PropTypes.object,
}

const AddPartnerDrawer = ({ isOpen, onClose, onPartnerAdded, groups = [], initialPartnerData = null }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirmClose, setShowConfirmClose] = useState(false)
  const groupNames = Array.from(new Set((groups || []).map(g => g.name))).sort()
  const [formData, setFormData] = useState({
    belongsToGroup: 'no',
    groupName: '',
  })
  const [selectedBusinessId, setSelectedBusinessId] = useState(null)

  // Default partner values - defined once and reused
  const getDefaultPartner = (id = 1) => ({
    id,
    partnerName: '',
    partnerType: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    spadeCounterPartyId: '',
    spadeLocationId: '',
    highnoteId: '',
    contracts: [
      {
        id: 1,
        startDate: ETDate.today().toISO(),
        endDate: null,
        rewardClass: 'IN_NETWORK',
        isNew: true,
      },
    ],
  })

  // Partners array to manage multiple partners
  const [partners, setPartners] = useState([getDefaultPartner(1)])

  // Counter for generating unique partner IDs
  const [nextPartnerId, setNextPartnerId] = useState(2)

  // Partner details form (Formik) - now works with partners array
  const partnerDetailsSchema = Yup.object({
    partners: Yup.array().of(
      Yup.object({
        partnerName: Yup.string().required('Partner Name is required'),
        partnerType: Yup.string().required('Partner Type is required'),
        street1: Yup.string().notRequired(),
        street2: Yup.string().notRequired(),
        city: Yup.string().notRequired(),
        state: Yup.string().notRequired(),
        // If provided, ZIP must be exactly 5 numeric digits
        zip: Yup.string()
          .test('zip-5-digits', 'ZIP Code must be exactly 5 digits', value => {
            if (!value) return true
            return /^\d{5}$/.test(String(value))
          })
          .notRequired(),
        spadeCounterPartyId: Yup.string()
          .test('is-csv', 'Must be a comma-separated list without spaces', validateCsv)
          .notRequired(),
        spadeLocationId: Yup.string()
          .test('is-csv', 'Must be a comma-separated list without spaces', validateCsv)
          .notRequired(),
        highnoteId: Yup.string()
          .test('is-csv', 'Must be a comma-separated list without spaces', validateCsv)
          .notRequired(),
        contracts: Yup.array()
          .of(
            Yup.object({
              startDate: Yup.string()
                .required('Start Date is required')
                .test(
                  'unique-start-date',
                  'Start Date must be unique within this business location',
                  function (startDate) {
                    if (!startDate) return true

                    // Get all contracts from the current partner
                    // Path format: "partners[0].contracts[1].startDate"
                    const partnerMatch = this.path.match(/partners\[(\d+)\]/)
                    const partnerIndex = partnerMatch ? parseInt(partnerMatch[1]) : 0
                    const allPartners = this.from[this.from.length - 1].value.partners || []
                    const currentPartner = allPartners[partnerIndex]
                    const contracts = currentPartner?.contracts || []

                    // Count how many contracts have this start date
                    const duplicateCount = contracts.filter(contract => contract.startDate === startDate).length

                    return duplicateCount <= 1
                  }
                ),
              endDate: Yup.string()
                .nullable()
                .notRequired()
                .test('end-after-start', 'End Date must be on or after Start Date', function (endDate) {
                  const { startDate } = this.parent
                  if (!endDate || !startDate) return true

                  // Both dates are now strings in yyyy-MM-dd format
                  const endETDate = ETDate.fromISO(endDate)
                  const startETDate = ETDate.fromISO(startDate)

                  return endETDate.greaterThanOrEqual(startETDate)
                }),
              rewardClass: Yup.string().required('Reward Class is required'),
            })
          )
          .min(1, 'At least one contract is required'),
      })
    ),
    groupName: Yup.string().when('belongsToGroup', {
      is: 'yes',
      then: schema => schema.required('Group Name is required'),
      otherwise: schema => schema.notRequired(),
    }),
  })

  const partnerFormik = useFormik({
    initialValues: {
      partners: partners,
      groupName: '',
      belongsToGroup: 'no',
    },
    validationSchema: partnerDetailsSchema,
    onSubmit: () => {},
    enableReinitialize: true, // Reinitialize when initialValues change
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
  })

  // Update Formik when partners state changes
  useEffect(() => {
    if (partnerFormik.values.partners !== partners) {
      partnerFormik.setFieldValue('partners', partners)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners])

  // Reset form when drawer closes
  useEffect(() => {
    if (!isOpen) {
      resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Pre-fill form when initialPartnerData is provided
  useEffect(() => {
    if (isOpen && initialPartnerData) {
      setPartners(prevPartners => {
        return prevPartners.map((partner, index) => {
          if (index === 0) {
            return {
              ...partner,
              partnerName: initialPartnerData.merchantName || '',
              spadeCounterPartyId: initialPartnerData.spadeCounterPartyId || '',
              spadeLocationId: initialPartnerData.spadeLocationId || '',
              highnoteId: initialPartnerData.highnoteId || '',
            }
          }
          return partner
        })
      })
    }
  }, [isOpen, initialPartnerData])

  // Ensure validation state is computed immediately on open
  useEffect(() => {
    if (isOpen) {
      partnerFormik.validateForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Partner management functions
  const addPartner = () => {
    const newPartner = getDefaultPartner(nextPartnerId)
    const updatedPartners = [...partners, newPartner]
    setPartners(updatedPartners)
    setNextPartnerId(prev => prev + 1) // Increment the counter
  }

  const updatePartner = (partnerId, field, value) => {
    const updatedPartners = partners.map(partner =>
      partner.id === partnerId ? { ...partner, [field]: value } : partner
    )
    setPartners(updatedPartners)
  }

  const deletePartner = partnerId => {
    if (partners.length <= 1) return // Don't delete if only one partner
    const updatedPartners = partners.filter(partner => partner.id !== partnerId)
    setPartners(updatedPartners)
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Also update formik values for fields that affect validation
    if (field === 'belongsToGroup') {
      partnerFormik.setFieldValue('belongsToGroup', value)
    }
  }

  const hasAnyInput = () => {
    const partnersEntered = partners.some(partner =>
      Object.values(partner).some(value => String(value).trim() !== '' && value !== partner.id)
    )
    // Check formData but exclude belongsToGroup radio selection changes
    // eslint-disable-next-line no-unused-vars
    const { belongsToGroup, ...otherFormData } = formData
    const formDataEntered = Object.values(otherFormData).some(value => value !== '' && value !== 'Active')
    return formDataEntered || partnersEntered
  }

  const hasAnyErrors = () => {
    const errors = partnerFormik.errors
    if (!errors) return false
    if (errors.groupName) return true
    if (Array.isArray(errors.partners)) {
      return errors.partners.some(e => e && Object.values(e).some(Boolean))
    }
    return false
  }

  const isAddButtonDisabled = () => {
    // Disable while loading or whenever there are any validation errors (independent of touched)
    return isLoading || hasAnyErrors()
  }

  const handleCancel = e => {
    e?.preventDefault?.()
    if (hasAnyInput()) {
      // Show confirmation modal for unsaved changes
      setShowConfirmClose(true)
    } else {
      resetForm()
      onClose()
    }
  }

  // Actually close everything
  const handleConfirmClose = () => {
    setShowConfirmClose(false)
    resetForm()
    onClose()
  }

  const resetForm = () => {
    partnerFormik.resetForm()
    setFormData({
      belongsToGroup: 'no',
      groupName: '',
    })
    setPartners([getDefaultPartner(1)])
    setNextPartnerId(2) // Reset the counter
  }

  const handleContinue = () => {
    // validate partner details
    partnerFormik.validateForm().then(errors => {
      // Mark all fields as touched to show errors
      const allTouched = {}

      // Mark group-level fields as touched
      allTouched.groupName = true
      allTouched.belongsToGroup = true

      // Mark all partner fields as touched
      allTouched.partners = partners.map(p => ({
        partnerName: true,
        partnerType: true,
        street1: true,
        street2: true,
        city: true,
        state: true,
        zip: true,
        spadeCounterPartyId: true,
        spadeLocationId: true,
        highnoteId: true,
        contracts: p.contracts?.map(() => ({
          startDate: true,
          endDate: true,
          rewardClass: true,
        })),
      }))

      partnerFormik.setTouched(allTouched)

      const hasPartnerErrors = Object.keys(errors).length > 0

      if (hasPartnerErrors) return

      // If validation passes, add directly
      handleConfirmAdd()
    })
  }

  const handleConfirmAdd = async () => {
    if (isLoading) return
    setIsLoading(true)

    try {
      // Build payloads based on whether we are adding to an existing group or creating a new business
      // Use local state as the single source of truth for group name
      const selectedGroupName = (formData.groupName || '').trim()
      const normalizeSpaces = s => (s || '').trim().replace(/\s+/g, ' ')
      const matchedGroup = (groups || []).find(
        g => normalizeSpaces(g?.name || '').toLowerCase() === normalizeSpaces(selectedGroupName).toLowerCase()
      )
      const businessId = selectedBusinessId || matchedGroup?.businessId

      if (formData.belongsToGroup === 'yes' && businessId) {
        // Matching group exists -> add business-location(s) only
        for (const p of partners) {
          const locationDto = {
            businessLocationId: '',
            businessLocationName: p.partnerName,
            businessLocationType: p.partnerType,
            mids: [
              ...splitAndPrefix(p.spadeCounterPartyId, 'SPC_'),
              ...splitAndPrefix(p.spadeLocationId, 'SPL_'),
              ...splitAndPrefix(p.highnoteId, 'HN_'),
            ],
            isActive: true,
            rewardClasses: p.contracts.map(contract => ({
              startDate: contract.startDate,
              endDate: contract.endDate,
              rewardClass: contract.rewardClass,
            })),
            street1: p.street1,
            street2: p.street2,
            city: p.city,
            state: p.state,
            postalCode: p.zip,
            country: 'USA',
          }
          await apiAddBusinessLocation(businessId, locationDto)
        }
      } else {
        // Use POST business when belongsToGroup is 'no' OR when belongsToGroup is 'yes' but no matched group
        // If user chose "yes" but group doesn't exist, create a new business with the entered group name
        const businessNameForCreate = formData.belongsToGroup === 'yes' ? selectedGroupName : partners[0]?.partnerName
        const payload = {
          businessId: '',
          businessName: businessNameForCreate,
          ledgerEntityId: '',
          locations: partners.map(p => ({
            businessLocationId: '',
            businessLocationName: p.partnerName,
            businessLocationType: p.partnerType,
            mids: [
              ...splitAndPrefix(p.spadeCounterPartyId, 'SPC_'),
              ...splitAndPrefix(p.spadeLocationId, 'SPL_'),
              ...splitAndPrefix(p.highnoteId, 'HN_'),
            ],
            isActive: true,
            rewardClasses: p.contracts.map(contract => ({
              startDate: contract.startDate,
              endDate: contract.endDate,
              rewardClass: contract.rewardClass,
            })),
            street1: p.street1,
            street2: p.street2,
            city: p.city,
            state: p.state,
            postalCode: p.zip,
            country: 'USA',
          })),
        }
        await apiAddBusiness(payload)
      }

      const newPartners = partners.map(partner => ({
        id: Date.now() + Math.random(),
        ...formData,
        lastUpdated: new Date().toISOString().split('T')[0],
        details: { ...partner },
      }))

      onPartnerAdded(newPartners)
      toast.custom(() => <ToastMessage {...TOASTS.CREATE_PARTNER.GROUP_CREATE_SUCCESS} />)
      resetForm()
      onClose()
    } catch (error) {
      toast.custom(() => (
        <ToastMessage
          {...TOASTS.CREATE_PARTNER.GROUP_CREATE_FAILURE}
          description={error?.message || TOASTS.CREATE_PARTNER.GROUP_CREATE_FAILURE.description}
        />
      ))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Drawer isOpen={isOpen} onClose={handleCancel} title="Add Partner">
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto">
            <SectionHeader text="GROUP DETAILS" />
            <div className="p-6">
              <div className="flex flex-col gap-2">
                <SubsectionHeader text="Does this partner belong to a group?" />
                <div className="flex flex-col gap-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="belongsToGroup"
                      value="no"
                      checked={formData.belongsToGroup === 'no'}
                      onChange={e => handleInputChange('belongsToGroup', e.target.value)}
                      className="mt-1"
                    />
                    <span className="text-sm">No - This partner is independent</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="belongsToGroup"
                      value="yes"
                      checked={formData.belongsToGroup === 'yes'}
                      onChange={e => handleInputChange('belongsToGroup', e.target.value)}
                      className="mt-1"
                    />
                    <span className="text-sm">Yes - This partner belongs to a group</span>
                  </label>
                </div>
              </div>

              {/* Animated Group Name Input */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  formData.belongsToGroup === 'yes' ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="pt-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="groupName" className="text-sm font-medium text-[--gray-700]">
                      Group Name*
                    </Label>
                    <SearchInput
                      placeholder="Enter group name"
                      options={groupNames}
                      value={partnerFormik.values.groupName}
                      onChange={value => {
                        // Update only the value while typing; don't mark touched or validate yet
                        const trimmed = (value || '').trim()
                        // Keep Formik in sync for validation only
                        partnerFormik.setFieldValue('groupName', trimmed)
                        // Local state is the source of truth
                        setFormData(prev => ({ ...prev, groupName: trimmed }))
                        // Clear any previously selected business id when free-typing
                        setSelectedBusinessId(null)
                      }}
                      onSelect={value => {
                        // Set the final value when an option is chosen; let onBlur control touched/validation
                        const trimmed = (value || '').trim()
                        // Keep Formik in sync for validation
                        partnerFormik.setValues({
                          ...partnerFormik.values,
                          groupName: trimmed,
                        })
                        // Update local state source of truth
                        setFormData(prev => ({ ...prev, groupName: trimmed }))
                        // Persist the matched group's businessId to avoid any string mismatch issues later
                        const match = (groups || []).find(
                          g => (g?.name || '').trim().toLowerCase() === trimmed.toLowerCase()
                        )
                        setSelectedBusinessId(match?.businessId || null)
                      }}
                      onBlur={() => {
                        partnerFormik.setFieldTouched('groupName', true)
                        // Validate on blur only
                        partnerFormik.validateField('groupName')
                      }}
                      noResultsText="No existing groups found"
                      className={partnerFormik.errors.groupName && partnerFormik.touched.groupName ? '' : ''}
                      style={{
                        boxShadow: '0 1px 2px 0 rgba(16, 24, 40, 0.05)',
                        border:
                          partnerFormik.errors.groupName && partnerFormik.touched.groupName
                            ? '1px solid var(--Error-300, #FDA29B)'
                            : '1px solid var(--Gray-300, #D0D5DD)',
                      }}
                      hasError={Boolean(partnerFormik.errors.groupName && partnerFormik.touched.groupName)}
                      tabIndex={formData.belongsToGroup === 'yes' ? 0 : -1}
                    />
                    {partnerFormik.errors.groupName && partnerFormik.touched.groupName && (
                      <div className="text-red-500 text-sm mt-1.5">{partnerFormik.errors.groupName}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <SectionHeader text="PARTNER DETAILS" />
            <div>
              <div className="flex flex-col">
                {partners.map((partner, index) => (
                  <div key={partner.id}>
                    {partners.length > 1 && (
                      <div className="px-6">
                        <PartnerHeader
                          partnerNumber={index + 1}
                          onDelete={() => deletePartner(partner.id)}
                          canDelete={partners.length > 1}
                        />
                      </div>
                    )}
                    <div className="pt-4 px-6">
                      <PartnerSection
                        partner={partner}
                        partnerIndex={index}
                        updatePartner={updatePartner}
                        formik={partnerFormik}
                      />
                    </div>

                    {/* ContractGroup outside padded container for full-width header */}
                    <div className="mt-8 mx-8 mb-6">
                      <ContractGroup
                        contracts={partner.contracts}
                        onContractsChange={updatedContracts => {
                          updatePartner(partner.id, 'contracts', updatedContracts)
                          partnerFormik.setFieldValue(`partners.${index}.contracts`, updatedContracts)
                        }}
                        errors={partnerFormik.errors?.partners?.[index] || {}}
                      />
                    </div>

                    {/* Horizontal divider - show between partners */}
                    {index < partners.length - 1 && (
                      <div className="mt-8 mb-8">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                    )}

                    {/* Horizontal divider - show above Add Another Partner button */}
                    {formData.belongsToGroup === 'yes' && index === partners.length - 1 && partners.length < 10 && (
                      <div className="mt-8 mb-4">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                    )}

                    {/* Add Another Partner Button - Only show when belongs to group and this is the last partner */}
                    {formData.belongsToGroup === 'yes' && index === partners.length - 1 && partners.length < 10 && (
                      <div className="pt-4 pb-8 px-6">
                        <button
                          type="button"
                          className="w-full py-3 px-4 font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ring-offset-background transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm"
                          style={{
                            borderRadius: '8px',
                            border: '1px solid var(--Gray-300, #D0D5DD)',
                            background: 'var(--Base-White, #FFF)',
                            color: 'var(--Gray-700, #344054)',
                            fontSize: '16px',
                          }}
                          onClick={addPartner}
                        >
                          <img src={Icons.plusAddPartnerIcon} alt="Add" className="w-5 h-5" />
                          Add Another Partner
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex-shrink-0 p-6 border-t border-gray-200">
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleCancel} className="flex-1" type="button">
                Cancel
              </Button>
              <Button onClick={handleContinue} className="flex-1" disabled={isAddButtonDisabled()} type="button">
                Add
              </Button>
            </div>
          </div>
        </div>
      </Drawer>

      {/* Unsaved Changes Modal */}
      <Dialog open={showConfirmClose} onOpenChange={setShowConfirmClose}>
        <DialogContent className="max-w-[480px]">
          <RulesDialogHeader
            title="Close without saving"
            description="Are you sure you want to close without saving? Any changes will be lost."
            icon={Icons.annotationAlertIcon}
          />
          <div className="ml-[-24px] w-[111%]">
            <Divider />
          </div>
          <DialogFooter className="w-full">
            <Button
              variant="outline"
              className="w-full md:w-full h-[44px] text-[--gray-700] mt-1"
              onClick={() => setShowConfirmClose(false)}
              type="button"
            >
              Back
            </Button>
            <Button className="text-white w-full h-[44px] mt-1" onClick={handleConfirmClose} type="button">
              Close without saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

AddPartnerDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onPartnerAdded: PropTypes.func,
  groups: PropTypes.arrayOf(PropTypes.object),
  initialPartnerData: PropTypes.shape({
    spadeCounterPartyId: PropTypes.string,
    spadeLocationId: PropTypes.string,
    highnoteId: PropTypes.string,
  }),
}

export default AddPartnerDrawer
